import { useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { message } from 'antd';
import clsx from 'clsx';
import { Control, FieldErrors, UseFormHandleSubmit } from 'react-hook-form';

import { LoginPayload } from '@/domains/user/ports/payloads';
import LoginForm from '@/modules/users/components/LoginForm';
import { ThreeDotSpin } from '@/components';
import authBackground from '@images/auth-background.png';
import { generateQRCode } from '@/modules/users/viewmodels';
import { useCountDown } from '@/common/hooks';
import { useSocketManager } from '@/modules/users/hook';
import { AuthorizationData } from '@/modules/users/types';
import '@/core/firebase';
import { useSearchParams } from 'react-router-dom';

type TabType = 'login-qr-code' | 'login-form';

interface LoginQRCodeFormProps {
  control: Control<LoginPayload>;
  handleSubmit: UseFormHandleSubmit<LoginPayload>;
  onSubmit: (data: LoginPayload) => Promise<void>;
  errors: FieldErrors<LoginPayload>;
  isLoggingIn: boolean;
  buttonStyle: React.CSSProperties;
  inputStyle: React.CSSProperties;
  onUsePasswordless?: () => void;
}

interface QRLoginUser {
  name: string;
  username: string;
  id: string;
}

const LoginQRCodeForm: React.FC<LoginQRCodeFormProps> = ({
  control,
  handleSubmit,
  onSubmit,
  errors,
  isLoggingIn,
  buttonStyle,
  inputStyle,
  onUsePasswordless,
}) => {
  const [tab, setTab] = useState<TabType>('login-qr-code');
  const [isLoginSuccess, setIsLoginSuccess] = useState(false);
  const [loginUser, setLoginUser] = useState<QRLoginUser | null>(null);
  const [isVerifyingToken, setIsVerifyingToken] = useState(false);

  const { t } = useTranslation();
  const { qrCodeData, actionGenerateQRCode } = generateQRCode();
  const {
    countDown,
    isLoading: isCountdownLoading,
    isExpired,
    formattedTime,
    resetCountDown,
    stopCountDown,
  } = useCountDown(qrCodeData?.expiresIn);
  const [searchParams] = useSearchParams();
  const deniseUrl = searchParams.get('callbackUrl') ?? '';

  const isLoginQRCode = useMemo(() => tab === 'login-qr-code', [tab]);
  const isLoginForm = useMemo(() => tab === 'login-form', [tab]);

  const handleChangeTab = (tab: TabType) => {
    setTab(tab);
  };

  // Function to decode JWT token and extract user info
  const decodeJWTToken = (token: string): Record<string, unknown> | null => {
    try {
      const base64Url = token.split('.')[1];
      const base64 = base64Url.replace(/-/g, '+').replace(/_/g, '/');
      const jsonPayload = decodeURIComponent(
        atob(base64)
          .split('')
          .map((c) => '%' + ('00' + c.charCodeAt(0).toString(16)).slice(-2))
          .join(''),
      );
      return JSON.parse(jsonPayload);
    } catch (error) {
      return null;
    }
  };

  // Complete QR code login process
  const completeQRLogin = async (authData: AuthorizationData) => {
    if (isVerifyingToken) return; // Prevent multiple calls

    setIsVerifyingToken(true);

    try {
      // Decode the JWT to get user information
      const tokenPayload = decodeJWTToken(authData.accessToken);
      console.log('tokenPayload', tokenPayload);

      if (
        tokenPayload &&
        typeof tokenPayload.name === 'string' &&
        typeof tokenPayload.username === 'string'
      ) {
        // Set the user info for the success screen
        setLoginUser({
          name: tokenPayload.name,
          username: tokenPayload.username,
          id: typeof tokenPayload.id === 'string' ? (tokenPayload.id as string) : '',
        });

        setIsLoginSuccess(true);

        const authPayload = JSON.stringify({
          accessToken: authData.accessToken,
          refreshToken: authData.refreshToken,
        });

        const base64Param = btoa(authPayload);

        setTimeout(() => {
          message.success('Login successful!');
          window.location.href = `${deniseUrl}?auth=${base64Param}&isScanQrCode=true`;
        }, 2000);
      } else {
        throw new Error('Invalid token payload');
      }
    } catch (error: unknown) {
      message.error('QR Code login failed. Please try again.');
      setIsLoginSuccess(false);
      setLoginUser(null);
    } finally {
      setIsVerifyingToken(false);
    }
  };

  const socketManager = useSocketManager({
    // Handle authorization events - MAIN FEATURE
    onAuthorization: (authData: AuthorizationData) => {
      // Complete the login process with the received token
      message.success(t('common.approvedLoginSuccessfully'));
      completeQRLogin(authData);
    },

    // Handle denied authorization events - DENIED LOGIN THROUGH DENISE
    onDeniedAuthorization: () => {
      message.info(t('common.deniedLogin'));

      setIsVerifyingToken(true);

      setIsLoginSuccess(false);
      setLoginUser(null);
      stopCountDown();

      setIsVerifyingToken(false);
    },

    // Handle errors
    onError: () => {
      message.error(t('error.connectionError'));
      setIsLoginSuccess(false);
    },
  });

  useEffect(() => {
    (function fetchQRCodeData() {
      actionGenerateQRCode();
    })();
  }, []);

  // Connect to socket when QR code is generated and has loginToken
  useEffect(() => {
    if (qrCodeData?.loginToken && isLoginQRCode) {
      socketManager
        .connect(qrCodeData.loginToken)
        .then(() => {
          // Successfully connected to socket
        })
        .catch(() => {
          // Handle connection error appropriately
        });
    }

    // Disconnect when switching away from QR code tab or component unmounts
    return () => {
      if (socketManager.isConnected) {
        socketManager.disconnect();
      }
    };
  }, [qrCodeData?.loginToken, isLoginQRCode]);

  return (
    <div className='space-y-8'>
      <div className='flex rounded-lg bg-[#D9D9D991] p-1'>
        <button
          className={clsx(
            'w-1/2 text-center py-1.5 border-none rounded-lg transition-all duration-500 cursor-pointer',
            {
              'bg-white shadow-[0_4px_8px_0_rgba(0,0,0,0.15)]': isLoginQRCode,
              'bg-[#D9D9D991]': !isLoginQRCode,
            },
          )}
          onClick={() => handleChangeTab('login-qr-code')}
        >
          <span
            className={clsx('text-lg leading-[30px] font-medium transition-all duration-500', {
              'text-[#2C2B2B]': isLoginQRCode,
              'text-[#58595BB0]': !isLoginQRCode,
            })}
          >
            {t('common.withQRCode')}
          </span>
        </button>
        <button
          className={clsx(
            'w-1/2 text-center py-1.5 border-none rounded-lg transition-all duration-500 cursor-pointer',
            {
              'bg-white shadow-[0_4px_8px_0_rgba(0,0,0,0.15)]': isLoginForm,
              'bg-[#D9D9D991]': !isLoginForm,
            },
          )}
          onClick={() => handleChangeTab('login-form')}
        >
          <span
            className={clsx('text-lg leading-[30px] font-medium transition-all duration-500', {
              'text-[#2C2B2B]': isLoginForm,
              'text-[#58595BB0]': !isLoginForm,
            })}
          >
            {t('common.withAccount')}
          </span>
        </button>
      </div>

      <div className='relative overflow-hidden'>
        <div
          className={clsx('transition-all duration-500 ease-in-out transform', {
            'opacity-100 translate-y-0 scale-100': isLoginQRCode,
            'opacity-0 -translate-y-4 scale-95 absolute inset-0 pointer-events-none':
              !isLoginQRCode,
          })}
        >
          {isLoginSuccess ? (
            <div className='flex flex-col items-center mt-7'>
              <div className='relative w-[142px] aspect-square rounded-full overflow-hidden'>
                <img
                  src={authBackground}
                  alt='Auth Background'
                  loading='lazy'
                  className='w-full h-full object-cover'
                />
                <p className='absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 text-white text-5xl font-semibold'>
                  {loginUser?.name
                    ? loginUser.name.charAt(0).toUpperCase() +
                      (loginUser.name.split(' ').pop()?.charAt(0).toUpperCase() || '')
                    : 'U'}
                </p>
              </div>

              <p className='text-2xl font-bold text-[#010101B2] pt-3'>
                {loginUser?.name || 'User'}
              </p>

              <p className='text-center pt-16 text-[#010101B2]'>
                {t('common.loginWithQRCodeSuccess')}
              </p>
            </div>
          ) : (
            <div className='flex flex-col items-center space-y-8 mt-2'>
              {isVerifyingToken ? (
                <div className='flex flex-col items-center space-y-4'>
                  <div className='w-[228px] h-[228px] rounded-lg shadow-[0_4px_12px_rgba(0,0,0,0.1)] flex items-center justify-center bg-blue-50'>
                    <ThreeDotSpin />
                  </div>
                  <p className='text-lg text-blue-600 font-medium animate-pulse'>
                    Verifying QR Code...
                  </p>
                </div>
              ) : qrCodeData ? (
                <div>
                  <div className='relative'>
                    <img
                      src={qrCodeData.qrCodeData ?? ''}
                      alt='QR Code'
                      loading='lazy'
                      className={clsx('rounded-lg shadow-[0_4px_12px_rgba(0,0,0,0.1)]', {
                        'blur-sm': isExpired,
                      })}
                    />

                    {isExpired && (
                      <div className='absolute inset-0 flex items-center justify-center'>
                        <button
                          onClick={() => {
                            actionGenerateQRCode();
                            resetCountDown();
                          }}
                          className='px-4 py-2 bg-[#cc1e1e] text-white rounded-lg text-sm hover:opacity-90 transition-all duration-500 cursor-pointer border-none shadow-[0_4px_8px_0_rgba(0,0,0,0.5)]'
                        >
                          {t('common.generateNewQR')}
                        </button>
                      </div>
                    )}
                  </div>

                  {/* Countdown Timer */}
                  <div className='mt-3 text-center'>
                    <div
                      className={clsx(
                        'inline-flex items-center gap-2 px-3 py-1 rounded-full text-sm font-medium transition-all duration-500',
                        {
                          'bg-red-100 text-red-600': isExpired,
                          'bg-blue-100 text-blue-600': !isExpired && countDown !== null,
                          'bg-gray-100 text-gray-500': isCountdownLoading,
                        },
                      )}
                    >
                      <span className='w-2 h-2 rounded-full bg-current animate-pulse' />
                      {isExpired ? t('common.qrCodeExpired') : formattedTime}
                    </div>
                  </div>
                </div>
              ) : (
                <div className='w-[228px] h-[228px] rounded-lg shadow-[0_4px_12px_rgba(0,0,0,0.1)] opacity-50 flex items-center justify-center'>
                  <ThreeDotSpin />
                </div>
              )}

              <p
                className={clsx('text-lg text-[#010101EB] transition-all duration-500 delay-200', {
                  'opacity-100': qrCodeData,
                  'opacity-50': !qrCodeData,
                })}
              >
                {t('common.scanQRByCompassToLogin')}
              </p>
            </div>
          )}
        </div>

        <div
          className={clsx('transition-all duration-500 ease-in-out transform', {
            'opacity-100 translate-y-0 scale-100': isLoginForm,
            'opacity-0 -translate-y-4 scale-95 absolute inset-0 pointer-events-none': !isLoginForm,
          })}
        >
          <LoginForm
            control={control}
            handleSubmit={handleSubmit}
            onSubmit={onSubmit}
            errors={errors}
            isLoggingIn={isLoggingIn}
            buttonStyle={buttonStyle}
            inputStyle={inputStyle}
            onUsePasswordless={onUsePasswordless}
          />
        </div>
      </div>
    </div>
  );
};

export default LoginQRCodeForm;
